/**
 * Text & Image Content Slide
 * Displays text content with an optional image
 * Supports single-column and two-column layouts
 */

import type { ContentSlideProps } from '../types';
import { stripHtml } from '../../../../utils/slides/stripHtml';

export default function TextImageSlide({ slide, className = '', hideHeading }: ContentSlideProps) {
  const { content } = slide;
  const isTwoColumn = content?.layout === 'two-column';
  const displayTitle = content?.title || content?.heading;

  if (isTwoColumn) {
    return (
      <div className={`mb-8 max-w-5xl mx-auto ${className}`}>
        <style>{`
          .rich-text-content h1 {
            font-size: 2em;
            font-weight: bold;
            margin-bottom: 0.5em;
          }
          .rich-text-content h2 {
            font-size: 1.5em;
            font-weight: bold;
            margin-bottom: 0.5em;
          }
          .rich-text-content h3 {
            font-size: 1.25em;
            font-weight: bold;
            margin-bottom: 0.5em;
          }
          .rich-text-content strong {
            font-weight: bold;
          }
          .rich-text-content em {
            font-style: italic;
          }
          .rich-text-content u {
            text-decoration: underline;
          }
          .rich-text-content ul {
            list-style-type: disc;
            padding-left: 2em;
            margin-bottom: 1em;
          }
          .rich-text-content ol {
            list-style-type: decimal;
            padding-left: 2em;
            margin-bottom: 1em;
          }
          .rich-text-content li {
            margin-bottom: 0.5em;
          }
          .rich-text-content p {
            margin-bottom: 1em;
          }
          .rich-text-content .ql-align-center {
            text-align: center;
          }
          .rich-text-content .ql-align-right {
            text-align: right;
          }
          .rich-text-content .ql-align-left {
            text-align: left;
          }
          .rich-text-content .ql-align-justify {
            text-align: justify;
          }
          .responsive-image {
            width: 100%;
            height: auto;
            min-height: 250px;
            max-height: 60vh;
            object-fit: contain;
            object-position: center;
          }
          @media (max-width: 640px) {
            .responsive-image {
              min-height: 200px;
              max-height: 50vh;
            }
          }
          @media (min-width: 1024px) {
            .responsive-image {
              min-height: 300px;
              max-height: 65vh;
            }
          }
        `}</style>
        {!hideHeading && displayTitle && (
          <h2 className="text-3xl font-bold mb-6 text-center">
            {stripHtml(displayTitle)}
          </h2>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Column 1 */}
          <div className="space-y-4">
            {content?.imageUrl && (
              <img
                src={content.imageUrl}
                alt={displayTitle || 'Content'}
                className="responsive-image w-full rounded-2xl border-2 border-border shadow-lg"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  (e.currentTarget as any).style.display = 'none';
                }}
              />
            )}
            {content?.content && (
              <div className="prose prose-sm max-w-none">
                <div 
                  className="text-lg text-foreground leading-relaxed rich-text-content"
                  dangerouslySetInnerHTML={{ __html: content.content }}
                />
              </div>
            )}
          </div>
          {/* Column 2 */}
          <div className="space-y-4">
            {content?.imageUrl2 && (
              <img
                src={content.imageUrl2}
                alt={displayTitle || 'Content 2'}
                className="responsive-image w-full rounded-2xl border-2 border-border shadow-lg"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  (e.currentTarget as any).style.display = 'none';
                }}
              />
            )}
            {content?.content2 && (
              <div className="prose prose-sm max-w-none">
                <div 
                  className="text-lg text-foreground leading-relaxed rich-text-content"
                  dangerouslySetInnerHTML={{ __html: content.content2 }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mb-8 max-w-3xl mx-auto ${className}`}>
      <style>{`
        .rich-text-content h1 {
          font-size: 2em;
          font-weight: bold;
          margin-bottom: 0.5em;
        }
        .rich-text-content h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin-bottom: 0.5em;
        }
        .rich-text-content h3 {
          font-size: 1.25em;
          font-weight: bold;
          margin-bottom: 0.5em;
        }
        .rich-text-content strong {
          font-weight: bold;
        }
        .rich-text-content em {
          font-style: italic;
        }
        .rich-text-content u {
          text-decoration: underline;
        }
        .rich-text-content ul {
          list-style-type: disc;
          padding-left: 2em;
          margin-bottom: 1em;
        }
        .rich-text-content ol {
          list-style-type: decimal;
          padding-left: 2em;
          margin-bottom: 1em;
        }
        .rich-text-content li {
          margin-bottom: 0.5em;
        }
        .rich-text-content p {
          margin-bottom: 1em;
        }
        .rich-text-content .ql-align-center {
          text-align: center;
        }
        .rich-text-content .ql-align-right {
          text-align: right;
        }
        .rich-text-content .ql-align-left {
          text-align: left;
        }
        .rich-text-content .ql-align-justify {
          text-align: justify;
        }
        .responsive-image {
          width: 100%;
          height: auto;
          min-height: 250px;
          max-height: 60vh;
          object-fit: contain;
          object-position: center;
        }
        @media (max-width: 640px) {
          .responsive-image {
            min-height: 200px;
            max-height: 50vh;
          }
        }
        @media (min-width: 1024px) {
          .responsive-image {
            min-height: 300px;
            max-height: 65vh;
          }
        }
      `}</style>
      {!hideHeading && displayTitle && (
        <h2 className="text-3xl font-bold mb-4 text-center">
          {stripHtml(displayTitle)}
        </h2>
      )}
      {content?.imageUrl && (
        <div className="mb-6">
          <img
            src={content.imageUrl}
            alt={displayTitle || 'Content'}
            className="responsive-image w-full rounded-2xl border-2 border-border shadow-lg"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              (e.currentTarget as any).style.display = 'none';
            }}
          />
        </div>
      )}
      {content?.content && (
        <div className="prose prose-sm max-w-none">
          <div 
            className="text-lg text-foreground leading-relaxed rich-text-content"
            dangerouslySetInnerHTML={{ __html: content.content }}
          />
        </div>
      )}
    </div>
  );
}
